import { Box, Typography } from "@mui/material";
import {
  HomeOutlined as HomeIcon,
  DirectionsCarOutlined as CarIcon,
  ShieldOutlined as ShieldIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { colorsFoundation } from "../../../theme/index.js";
import { familyOf } from "../../../formdefs/claimWizard/familyMap.js";

const ACCENT = "#4A78BC";

const familyIcon = (sparte) => {
  const fam = familyOf(sparte);
  if (fam === "kfz") return CarIcon;
  if (fam === "haftpflicht") return ShieldIcon;
  return HomeIcon;
};

/**
 * "Welcher Vertrag ist betroffen?" — shows the customer's contract(s) as cards
 * (up to four per line) plus a full-width "Ich bin mir nicht sicher" option, so
 * the policyholder can defer the classification to their broker. Matches the
 * Schmidt click dummy.
 */
export default function ClaimContractCard({
  model = {},
  values = {},
  onChange,
  disabled = false,
}) {
  // The create flow enters with one route-selected contract; support a list too.
  const contracts =
    Array.isArray(model.contracts) && model.contracts.length
      ? model.contracts
      : [
          {
            id: model.vertrag_id_ov || "current",
            name: model.sparte || "Vertrag",
            insurer: model.gesellschaft || "",
            number: values.vertragsnummer || model.vertragsnummer || "",
            sparte: model.sparte,
          },
        ];

  const unsure = Boolean(values.vertrag_unsicher);
  const set = (name, value) => onChange?.({ target: { name, value } });

  const CardIcon = (sparte) => {
    const Icon = familyIcon(sparte);
    return <Icon sx={{ fontSize: 18, color: ACCENT }} />;
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Contract cards — up to four per line */}
      <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        {contracts.map((c) => {
          const selected = !unsure;
          return (
            <Box
              key={c.id}
              component="button"
              type="button"
              disabled={disabled}
              onClick={() => !disabled && set("vertrag_unsicher", false)}
              sx={{
                flex: "1 1 calc(25% - 9px)",
                minWidth: "175px",
                textAlign: "left",
                cursor: disabled ? "default" : "pointer",
                fontFamily: "inherit",
                p: "15px 16px",
                borderRadius: "12px",
                border: `${selected ? 1.5 : 1}px solid ${
                  selected ? ACCENT : "#e7edf4"
                }`,
                backgroundColor: selected ? "#f4f8fd" : "#fff",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  mb: "10px",
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "9px",
                    flex: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#EEF3FB",
                  }}
                >
                  {CardIcon(c.sparte)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "13.5px",
                      fontWeight: 700,
                      color: colorsFoundation.gray[900],
                    }}
                  >
                    {c.name}
                  </Typography>
                  <Typography sx={{ fontSize: "11.5px", color: "#8a97a8" }}>
                    {c.insurer}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                  {c.number}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    fontSize: "10.5px",
                    fontWeight: 700,
                    color: "#1F8A5B",
                    backgroundColor: "#E7F4EC",
                    borderRadius: "20px",
                    px: "9px",
                    py: "2px",
                  }}
                >
                  aktiv
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* "Ich bin mir nicht sicher" — full width, explanation inside */}
      <Box
        component="button"
        type="button"
        disabled={disabled}
        onClick={() => !disabled && set("vertrag_unsicher", true)}
        sx={{
          width: "100%",
          textAlign: "left",
          cursor: disabled ? "default" : "pointer",
          fontFamily: "inherit",
          display: "flex",
          alignItems: "flex-start",
          gap: "11px",
          p: "13px 15px",
          borderRadius: "11px",
          border: `1.5px solid ${unsure ? ACCENT : "#dde3ec"}`,
          backgroundColor: unsure ? "#f4f8fd" : "#fff",
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            flex: "none",
            mt: "1px",
            boxSizing: "border-box",
            border: unsure ? `5px solid ${ACCENT}` : "2px solid #cbd5e1",
          }}
        />
        <Box>
          <Typography
            sx={{
              fontSize: "13.5px",
              fontWeight: 600,
              color: colorsFoundation.gray[900],
            }}
          >
            Ich bin mir nicht sicher
          </Typography>
          <Typography sx={{ fontSize: "12.5px", color: "#6b7280", mt: "1px" }}>
            Kein Problem – Ihr Ansprechpartner ordnet den Schaden korrekt zu.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

ClaimContractCard.propTypes = {
  model: PropTypes.object,
  values: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};
